import type {
  BuyerInterviewDto,
  BuyerInterviewQuestionDto,
  BuyerInterviewRegistryDto,
  BuyerQualificationDimension,
  QualificationVerdict,
} from "@/api/buyers";
import {
  DIMENSION_ORDER,
  DIMENSION_SECTION,
  describeAnswer,
  isAsked,
  isRecorded,
  questionsForSection,
  sectionTitle,
  toDraftAnswers,
  type DraftAnswers,
} from "./interviewBits";

/**
 * Two completed interviews, side by side.
 *
 * <p>🔴 <b>Nothing here scores anything.</b> Each side's category results are
 * the ones the server computed for that interview; this only lines them up and
 * says which answers read differently. A category compares its COMPUTED result
 * — an override is a roll-up decision, not a category one, and it shows on the
 * overall line instead.</p>
 *
 * <p>🔴 <b>Each side reads in its OWN wording.</b> The two interviews may have
 * been recorded under different wording versions, so each side is rendered
 * from the registry of its own `wordingVersion`. Whether an answer CHANGED is
 * decided on what was recorded — the option keys and the free text — never on
 * the rendered strings: a relabelled choice is the same answer, and two
 * different keys that happen to share a label are not.</p>
 */

export const NOT_ASKED = "Not asked";
export const NOT_RECORDED = "Not recorded";

export interface CompareAnswerRow {
  questionId: string;
  /** The question as each side's wording asked it. */
  leftLabel: string;
  rightLabel: string;
  left: string;
  right: string;
  changed: boolean;
}

export interface CompareCategory {
  dimension: BuyerQualificationDimension;
  /** The section's heading in each side's wording (ruling Q3). */
  leftTitle: string;
  rightTitle: string;
  left: QualificationVerdict | null;
  right: QualificationVerdict | null;
  resultChanged: boolean;
  answers: CompareAnswerRow[];
}

/** One answer as the comparison prints it. A branch that never opened on this
 *  side, or a retired question this side recorded nothing against, is "Not
 *  asked"; an asked question left blank is "Not recorded". */
export function answerText(
  question: BuyerInterviewQuestionDto | undefined,
  answers: DraftAnswers,
): string {
  if (!question || !isAsked(question, answers)) return NOT_ASKED;
  const answer = answers[question.id];
  return isRecorded(answer) ? describeAnswer(question, answer) : NOT_RECORDED;
}

/** What was recorded, as the comparison judges it: null when not asked;
 *  otherwise the chosen KEYS (order-free) and the trimmed text. */
function recordedState(
  question: BuyerInterviewQuestionDto | undefined,
  answers: DraftAnswers,
): string | null {
  if (!question || !isAsked(question, answers)) return null;
  const answer = answers[question.id];
  const keys = [...(answer?.choices ?? [])].sort();
  return JSON.stringify([keys, answer?.text.trim() ?? ""]);
}

export function compareInterviews(
  left: BuyerInterviewDto,
  right: BuyerInterviewDto,
  leftRegistry: BuyerInterviewRegistryDto,
  rightRegistry: BuyerInterviewRegistryDto,
): CompareCategory[] {
  const leftAnswers = toDraftAnswers(left.answers);
  const rightAnswers = toDraftAnswers(right.answers);
  const verdictOf = (interview: BuyerInterviewDto, d: BuyerQualificationDimension) =>
    interview.outcome?.dimensions.find((x) => x.dimension === d)?.verdict ?? null;

  return DIMENSION_ORDER.map((dimension) => {
    const sectionId = DIMENSION_SECTION[dimension];
    const leftVerdict = verdictOf(left, dimension);
    const rightVerdict = verdictOf(right, dimension);
    const leftQuestions = new Map(
      questionsForSection(leftRegistry, sectionId).map((q) => [q.id, q]),
    );
    const rightQuestions = new Map(
      questionsForSection(rightRegistry, sectionId).map((q) => [q.id, q]),
    );
    // Both wordings render the same question set; the union is defensive, in
    // the newer side's order.
    const ids = [...new Set([...rightQuestions.keys(), ...leftQuestions.keys()])];

    const answers = ids
      .map((id) => ({ id, l: leftQuestions.get(id), r: rightQuestions.get(id) }))
      // A question asked on neither side is not part of either interview.
      .filter(
        ({ l, r }) =>
          (l != null && isAsked(l, leftAnswers)) || (r != null && isAsked(r, rightAnswers)),
      )
      .map(({ id, l, r }) => ({
        questionId: id,
        leftLabel: (l ?? r)!.label,
        rightLabel: (r ?? l)!.label,
        left: answerText(l, leftAnswers),
        right: answerText(r, rightAnswers),
        changed: recordedState(l, leftAnswers) !== recordedState(r, rightAnswers),
      }));
    return {
      dimension,
      leftTitle: sectionTitle(leftRegistry, sectionId),
      rightTitle: sectionTitle(rightRegistry, sectionId),
      left: leftVerdict,
      right: rightVerdict,
      resultChanged: leftVerdict !== rightVerdict,
      answers,
    };
  });
}

/** The version the picker opens on: the completed version just before this
 *  one, else (viewing the oldest) the nearest one after it. */
export function defaultCompareVersion(current: number, completed: number[]): number | null {
  const others = completed.filter((v) => v !== current);
  if (others.length === 0) return null;
  const earlier = others.filter((v) => v < current);
  if (earlier.length > 0) return Math.max(...earlier);
  return Math.min(...others);
}
